import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Star, ArrowLeft, ArrowRight, Truck, MapPin, Calculator } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiErrorMessage } from "@/lib/api";
import { LogoMark, Wordmark } from "@/components/Logo";
import { FadeUp } from "@/components/Reveal";
import { AvailabilityBadge } from "./marketplace/shared";

function TopBar() {
  return (
    <header className="border-b border-white/[0.06] bg-black/60 backdrop-blur sticky top-0 z-20">
      <div className="px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <LogoMark size={30} />
          <Wordmark className="text-lg" />
        </Link>
        <div className="flex items-center gap-3 text-xs uppercase tracking-widest font-bold">
          <Link to="/login" className="text-zinc-400 hover:text-white transition-colors">Sign in</Link>
          <Link to="/signup" className="bg-[#FF3B30] hover:bg-[#D62828] text-white px-4 py-2.5 transition-colors">
            Start free
          </Link>
        </div>
      </div>
    </header>
  );
}

export function DriversDirectory() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/drivers?limit=100");
        setDrivers(data);
      } catch (err) {
        toast.error(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const visible = verifiedOnly ? drivers.filter((d) => d.verified) : drivers;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="drivers-directory">
      <TopBar />
      <main className="px-6 md:px-10 lg:px-14 py-16 lg:py-24 max-w-6xl mx-auto">
        <FadeUp>
          <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Directory</span>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black uppercase leading-[0.95] mt-4">
            Drivers on the platform
          </h1>
          <p className="text-zinc-400 mt-6 max-w-2xl text-base md:text-lg leading-relaxed">
            Every driver is verified before their first bid — license, insurance, DOT. Public reputation. Direct payment.
          </p>
        </FadeUp>

        <button
          onClick={() => setVerifiedOnly((v) => !v)}
          data-testid="filter-verified-only"
          className={`mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 border transition-colors ${
            verifiedOnly
              ? "border-[#FF3B30] bg-[#FF3B30] text-white"
              : "border-white/15 text-zinc-400 hover:border-white hover:text-white"
          }`}
        >
          <ShieldCheck className="w-3.5 h-3.5" /> Verified drivers only
        </button>

        <div className="mt-14">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="border border-white/[0.06] bg-[#0F0F10] h-56 animate-pulse" />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="border border-dashed border-white/[0.08] p-12 text-center bg-[#0F0F10]">
              <div className="font-display text-2xl font-black uppercase">
                {verifiedOnly ? "No verified drivers yet" : "No drivers yet"}
              </div>
              <p className="text-zinc-500 text-sm mt-2">
                {verifiedOnly ? "Check back soon — verification is ongoing." : "Be the first — apply to drive today."}
              </p>
              <Link
                to="/driver-signup"
                className="mt-6 inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold uppercase tracking-widest text-xs py-3 px-5 border-2 border-[#FF3B30]"
              >
                Become a driver <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {visible.map((d, i) => (
                <FadeUp key={d.id} delay={i * 0.05}>
                  <Link
                    to={`/drivers/${d.id}`}
                    data-testid={`driver-card-${d.id}`}
                    className="group block border border-white/[0.08] bg-[#0F0F10] p-6 hover:border-[#FF3B30] transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-[#FF3B30] text-black font-display text-2xl font-black flex items-center justify-center uppercase">
                        {d.name?.slice(0, 2) || "DR"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="font-display text-lg font-black uppercase truncate">{d.name}</div>
                          {d.verified && <ShieldCheck className="w-4 h-4 text-[#FF3B30] shrink-0" strokeWidth={2.5} />}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mt-1">
                          {d.verified ? "Verified driver" : "New driver"}
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 flex items-center gap-4 text-sm text-zinc-400">
                      <div className="flex items-center gap-1.5">
                        <Star className="w-3.5 h-3.5 text-[#FF3B30]" fill="#FF3B30" />
                        <span className="font-semibold text-white">
                          {d.rating_avg ? d.rating_avg.toFixed(1) : "—"}
                        </span>
                        <span className="text-zinc-500">({d.rating_count})</span>
                      </div>
                      <div className="w-px h-3 bg-white/15" />
                      <span>{d.completed_jobs} completed</span>
                    </div>
                    <div className="mt-3">
                      <AvailabilityBadge
                        status={d.availability_status}
                        blackoutUntil={(() => {
                          const today = new Date().toISOString().slice(0, 10);
                          const cur = (d.blackout_ranges || []).find((r) => r.start <= today && today <= r.end);
                          return cur ? cur.end : null;
                        })()}
                        testid={`driver-availability-${d.id}`}
                      />
                    </div>
                    {d.bio && <p className="text-sm text-zinc-500 leading-relaxed mt-4 line-clamp-3">{d.bio}</p>}
                    <div className="mt-6 text-[11px] uppercase tracking-widest text-[#FF3B30] font-bold flex items-center gap-2 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all">
                      View passport <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </Link>
                </FadeUp>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export function DriverProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [driver, setDriver] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const [d, r] = await Promise.all([
          api.get(`/drivers/${id}`),
          api.get(`/ratings/user/${id}`),
        ]);
        setDriver(d.data);
        setRatings(r.data);
      } catch (err) {
        toast.error(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <TopBar />
        <div className="p-14 text-zinc-500 uppercase text-xs tracking-widest animate-pulse">Loading passport…</div>
      </div>
    );
  }
  if (!driver) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <TopBar />
        <div className="p-14">
          <h1 className="font-display text-3xl font-black uppercase">Driver not found</h1>
          <Link to="/drivers" className="mt-4 inline-flex items-center gap-2 text-[#FF3B30] hover:text-white text-sm uppercase tracking-widest font-bold">
            <ArrowLeft className="w-4 h-4" /> Back to directory
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="driver-passport">
      <TopBar />
      <main className="px-6 md:px-10 lg:px-14 py-16 lg:py-24 max-w-4xl mx-auto">
        <Link to="/drivers" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] transition-colors mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> All drivers
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-start gap-6 flex-wrap"
        >
          <div className="w-20 h-20 bg-[#FF3B30] text-black font-display text-4xl font-black flex items-center justify-center uppercase">
            {driver.name?.slice(0, 2) || "DR"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="font-display text-4xl md:text-5xl font-black uppercase leading-none">{driver.name}</h1>
              {driver.verified && (
                <span className="inline-flex items-center gap-1.5 bg-[#FF3B30] text-black text-[10px] uppercase tracking-widest font-black px-2 py-1">
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
            <div className="mt-3 flex items-center gap-4 text-sm text-zinc-400">
              <div className="flex items-center gap-1.5">
                <Star className="w-4 h-4 text-[#FF3B30]" fill="#FF3B30" />
                <span className="font-semibold text-white">
                  {driver.rating_avg ? driver.rating_avg.toFixed(1) : "—"}
                </span>
                <span className="text-zinc-500">({driver.rating_count} reviews)</span>
              </div>
              <div className="w-px h-4 bg-white/15" />
              <span>{driver.completed_jobs} completed jobs</span>
            </div>
          </div>
        </motion.div>

        {user?.role === "driver" && <ProfitCalculatorCard />}

        {driver.bio && (
          <section className="mt-10">
            <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ About</span>
            <p className="text-zinc-300 leading-relaxed mt-3 whitespace-pre-wrap">{driver.bio}</p>
          </section>
        )}

        {(driver.truck_make || driver.truck_model || driver.max_length_ft != null || driver.max_weight_lbs != null || (driver.hitch_classes && driver.hitch_classes.length) || (driver.gear && driver.gear.length)) && (
          <section className="mt-10" data-testid="driver-fleet">
            <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Fleet</span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {(driver.truck_make || driver.truck_model) && (
                <FleetCell label="Truck" value={`${driver.truck_year || ""} ${driver.truck_make} ${driver.truck_model}`.trim()} />
              )}
              {driver.max_length_ft != null && (
                <FleetCell label="Max trailer length" value={`${driver.max_length_ft} ft`} />
              )}
              {driver.max_weight_lbs != null && (
                <FleetCell label="Max tow weight" value={`${driver.max_weight_lbs.toLocaleString()} lbs`} />
              )}
              {driver.hitch_classes && driver.hitch_classes.length > 0 && (
                <FleetCell label="Hitches" value={driver.hitch_classes.join(" · ")} />
              )}
              {driver.gear && driver.gear.length > 0 && (
                <FleetCell label="Gear" value={driver.gear.join(" · ")} span={2} />
              )}
            </div>
          </section>
        )}

        <section className="mt-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Reviews</span>
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-bold">({ratings.length})</span>
          </div>
          {ratings.length === 0 ? (
            <div className="border border-dashed border-white/[0.08] p-10 text-center bg-[#0F0F10]">
              <div className="font-display text-2xl font-black uppercase">No reviews yet</div>
              <p className="text-zinc-500 text-sm mt-2">This driver is fresh on the platform.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {ratings.map((r) => (
                <div key={r.id} className="border border-white/[0.08] bg-[#0F0F10] p-5">
                  <div className="flex items-center justify-between">
                    <div className="font-display text-base font-black uppercase">{r.from_user_name}</div>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className="w-4 h-4"
                          fill={n <= r.score ? "#FF3B30" : "none"}
                          color={n <= r.score ? "#FF3B30" : "#3F3F46"}
                        />
                      ))}
                    </div>
                  </div>
                  {r.comment && <p className="text-sm text-zinc-400 mt-2">{r.comment}</p>}
                  <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-3">
                    {new Date(r.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function FleetCell({ label, value, span = 1 }) {
  return (
    <div className={`border border-white/[0.08] bg-[#0F0F10] p-4 ${span === 2 ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{label}</div>
      <div className="text-white font-semibold mt-1">{value}</div>
    </div>
  );
}

function ProfitCalculatorCard() {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // backend mints a single-use 5-min ticket, then hands us the signed-in URL
      const { data } = await api.post("/auth/sso/profit-calculator");
      window.location.assign(data.url);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't open the profit calculator."));
      setBusy(false);
    }
  };
  return (
    <section className="mt-10 border border-white/[0.08] bg-[#0F0F10] p-6 flex items-center gap-5 flex-wrap" data-testid="profit-calculator-card">
      <div className="w-12 h-12 bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-center shrink-0">
        <Calculator className="w-5 h-5 text-[#FF3B30]" />
      </div>
      <div className="flex-1 min-w-[220px]">
        <h2 className="font-display text-lg font-black uppercase">Profit calculator</h2>
        <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
          Price your hauls before you bid. Registered drivers are signed in securely and automatically.
        </p>
      </div>
      <button
        onClick={open}
        disabled={busy}
        data-testid="profit-calculator-btn"
        className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] disabled:opacity-60 text-white px-5 py-3 text-[11px] uppercase tracking-widest font-bold transition-colors"
      >
        {busy ? "Signing you in…" : "Open calculator"} <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </section>
  );
}
